import { CustomMbscCalendarEventData, filterToDraggable, LocalVisitRequest } from "~/utils/eventsHelper";
import { LocalCalendarBlockBase, LocalShiftBase, LocalVisit, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { CommandIndexCareRequirementVisitsQuery, CommandIndexProvidersQuery, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import {
  createVisitDragDropData,
  validateVisitDragDrop,
} from "~/hooks/visitDragDropUtils";
import { processCareRequirements } from "~/utils/driveTimeOptimizerConversion";
import { DateLike, TimeFormatPreference, timeLabel } from "@caredna-marlin/utils/dates";
import { loadTimeFormatPreference } from "~/providers/ZonedDateFormatProvider";
import { groupBy, minBy } from "lodash";
import { visitResourceStatus } from "~/utils/statuses";
type VisitWarningOptions = {
  careTeamProviderIds?: number[];
};

export type CareRequirementWarningsByVisitResourceId = Record<number, string[]>;

const addWarning = (
  warningsByVisitResourceId: Record<number, Set<string>>,
  visitResourceId: number,
  warning: string,
) => {
  const currentWarnings = warningsByVisitResourceId[visitResourceId] ?? new Set<string>();
  currentWarnings.add(warning);
  warningsByVisitResourceId[visitResourceId] = currentWarnings;
};

const formatMinutesOrHours = (minutes: number): string => {
  if (Math.abs(minutes) <= 59) {
    return `${minutes} minute${Math.abs(minutes) === 1 ? "" : "s"}`;
  }

  const hours = minutes / 60;
  const formattedHours = Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
  return `${formattedHours} hour${Math.abs(hours) === 1 ? "" : "s"}`;
};

const formatTimeLabel = (
  timeFormat: TimeFormatPreference,
  startTime: DateLike,
  endTime?: DateLike,
  timezone?: string | null,
): string => {
  const options = { timezone, timeFormat: loadTimeFormatPreference() };
  if (!endTime) {
    return timeLabel(startTime, options);
  }
  return `${timeLabel(startTime, options)} and ${timeLabel(endTime, options)}`;
};

export const buildCareRequirementWarningsByVisitResourceId = (
  localVisitResources: LocalVisitResource[],
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
  now: Date = new Date(),
): CareRequirementWarningsByVisitResourceId => {
  if (!localVisitResources.length || !careRequirementVisits.length) {
    return {};
  }

  localVisitResources = localVisitResources.filter(vr => visitResourceStatus(vr) != VisitEventStatusEnum.Canceled);

  const { careRequirementOverridesByVisitId, proximityConstraints } = processCareRequirements(
    careRequirementVisits,
    now,
    localVisitResources,
    (resource) => !!resource.errandStatus,
  );

  const timeFormat = loadTimeFormatPreference();

  const warningsByVisitResourceId: Record<number, Set<string>> = {};
  const visitResourceById = Object.fromEntries(
    localVisitResources.map((visitResource) => [visitResource.id, visitResource])
  ) as Record<number, LocalVisitResource>;

  const resourcesByVisitId = groupBy(localVisitResources, "visitId");
  const earliestResourceIdByVisitId: Record<number, number | undefined> = Object.fromEntries(
    Object.entries(resourcesByVisitId).map(([visitId, resources]) => [
      Number(visitId),
      minBy(resources, "startTime")?.id,
    ])
  );

  localVisitResources.forEach((visitResource) => {
    const careRequirementOverride = careRequirementOverridesByVisitId[visitResource.visitId];
    const isEarliestResourceForVisit =
      earliestResourceIdByVisitId[visitResource.visitId] === visitResource.id;
    if (careRequirementOverride && isEarliestResourceForVisit) {
      const resourceStart = new Date(visitResource.startTime);
      const allowedWindowStart = careRequirementOverride.arrivalWindowStart ? new Date(careRequirementOverride.arrivalWindowStart) : undefined;
      const allowedWindowEnd = careRequirementOverride.arrivalWindowEnd ? new Date(careRequirementOverride.arrivalWindowEnd) : undefined;

      if (allowedWindowStart && resourceStart < allowedWindowStart) {
        addWarning(
          warningsByVisitResourceId,
          visitResource.id,
          `Clinical constraint: visit must be between ${formatTimeLabel(
            timeFormat,
            allowedWindowStart,
            allowedWindowEnd ?? allowedWindowStart,
            visitResource.visit.addressTimezone
          )}.`
        );
      }

      if (allowedWindowEnd && resourceStart > allowedWindowEnd) {
        addWarning(
          warningsByVisitResourceId,
          visitResource.id,
          `Clinical constraint: visit must be between ${formatTimeLabel(
            timeFormat,
            allowedWindowStart ?? allowedWindowEnd,
            allowedWindowEnd,
            visitResource.visit.addressTimezone
          )}.`
        );
      }
    }

    const resourceConstraints = proximityConstraints.filter((constraint) => constraint.laterVisitResourceId === visitResource.id);
    resourceConstraints.forEach((constraint) => {
      const earlierVisitResource = visitResourceById[constraint.earlierVisitResourceId];
      if (!earlierVisitResource) {
        return;
      }

      const minutesFromPreviousVisit = Math.round(
        (new Date(visitResource.startTime).getTime() - new Date(earlierVisitResource.startTime).getTime()) / 60000
      );

      if (minutesFromPreviousVisit < 0) {
        addWarning(
          warningsByVisitResourceId,
          visitResource.id,
          `Clinical constraint: visit is out of order. Needs to be after the visit at ${formatTimeLabel(timeFormat, earlierVisitResource.startTime)}.`
        );
        return;
      }

      if (
        constraint.minProximityMinutes != null &&
        minutesFromPreviousVisit < constraint.minProximityMinutes
      ) {
        addWarning(
          warningsByVisitResourceId,
          visitResource.id,
          `Clinical constraint: visit starts ${formatMinutesOrHours(minutesFromPreviousVisit)} after the previous visit; minimum is ${formatMinutesOrHours(constraint.minProximityMinutes)}.`
        );
      }

      if (
        constraint.maxProximityMinutes != null &&
        minutesFromPreviousVisit > constraint.maxProximityMinutes
      ) {
        addWarning(
          warningsByVisitResourceId,
          visitResource.id,
          `Clinical constraint: visit starts ${formatMinutesOrHours(minutesFromPreviousVisit)} after the previous visit; maximum is ${formatMinutesOrHours(constraint.maxProximityMinutes)}.`
        );
      }
    });
  });

  return Object.fromEntries(
    Object.entries(warningsByVisitResourceId).map(([visitResourceId, warnings]) => [visitResourceId, [...warnings]])
  );
};

export const getVisitWarning = (
  event: CustomMbscCalendarEventData,
  localShifts: LocalShiftBase,
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localCalendarBlocks: LocalCalendarBlockBase,
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  orgConfig: OrgConfig,
  options: VisitWarningOptions = {},
  careRequirementWarningsByVisitResourceId: CareRequirementWarningsByVisitResourceId = {}
) => {
  const draggableEvent = filterToDraggable(event, { allowNonDraggable: true });

  if (!draggableEvent) {
    return "";
  }

  const data = createVisitDragDropData(
    draggableEvent,
    orgConfig,
    localVisitResources,
    localVisits,
    localVisitRequests,
    localShifts,
    localCalendarBlocks,
    providers,
  );

  const careTeamProviderIds = options.careTeamProviderIds ?? [];
  const validationErrors = validateVisitDragDrop(data, careTeamProviderIds);
  const careRequirementWarnings = event.type === "visit_resource"
    ? (careRequirementWarningsByVisitResourceId[event.referenceId] ?? [])
    : [];

  return [
    ...validationErrors
    .filter(error => !error.shouldAllowDrop)
    .map(error => error.message),
    ...careRequirementWarnings,
  ].join("\n");
};
